import React from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import { motion } from "framer-motion";

import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import FormatListBulletedOutlinedIcon from "@mui/icons-material/FormatListBulletedOutlined";
import RouteOutlinedIcon from "@mui/icons-material/RouteOutlined";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";

const communityGroups = [
  {
    text: "Give your evangelism team one shared view of the people and households you are reaching",
    icon: GroupsOutlinedIcon,
  },
  {
    text: "Understand where relationships and outreach opportunities exist across your community",
    icon: FilterAltOutlinedIcon,
  },
  {
    text: "See previous conversations and interactions before someone follows up again",
    icon: HistoryOutlinedIcon,
  },
  {
    text: "Keep useful notes, prayer points and next steps with the ministry instead of in individual memories",
    icon: FormatListBulletedOutlinedIcon,
  },
  {
    text: "See where people are in their journey and what a helpful next step might be",
    icon: RouteOutlinedIcon,
  },
  {
    text: "Give staff and volunteers the context they need for warmer, more informed follow-up",
    icon: ForumOutlinedIcon,
  },
];

const CommunitySection = () => {
  return (
    <Box
      sx={{
        background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
        py: { xs: 10, md: 14 },
      }}
    >
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          <Typography
            variant="h3"
            textAlign="center"
            sx={{ color: "#ffffff", fontWeight: 800, mb: 2 }}
          >
            Give Your Team a Shared Ministry Memory
          </Typography>

          <Typography
            variant="h6"
            textAlign="center"
            sx={{
              color: "rgba(255,255,255,0.7)",
              maxWidth: 760,
              mx: "auto",
              mb: 6,
            }}
          >
            When your church is reaching more people than any one leader can
            personally know, PATHWAYS keeps relationships, conversations and
            useful context visible to the wider ministry.
          </Typography>
        </motion.div>

        <Grid2 container spacing={2}>
          {communityGroups.map(({ text, icon: Icon }, index) => (
            <Grid2 size={{ xs: 12, md: 4 }} key={text}>
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                style={{ height: "100%" }}
              >
                <Card
                  sx={{
                    background: "#1e293b",
                    borderRadius: 3,
                    boxShadow: "0 6px 20px rgba(0,0,0,0.25)",
                    height: "100%",
                    transition: "transform 0.3s ease, box-shadow 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 10px 28px rgba(0,0,0,0.35)",
                    },
                  }}
                >
                  <CardContent
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 2,
                      height: "100%",
                      p: 3,
                      "&:last-child": {
                        pb: 3,
                      },
                    }}
                  >
                    <Icon
                      sx={{
                        color: "#4ade80",
                        fontSize: 28,
                        flexShrink: 0,
                        mt: 0.1,
                      }}
                    />

                    <Typography
                      variant="body1"
                      sx={{
                        color: "#ffffff",
                        fontWeight: 600,
                        lineHeight: 1.6,
                      }}
                    >
                      {text}
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid2>
          ))}
        </Grid2>

        <Box
          sx={{
            position: "relative",
            mt: { xs: 8, md: 12 },
            height: { xs: 520, sm: 600, md: 460 },
            mx: "auto",
          }}
        >
          <Box
            component={motion.img}
            src="/Features/Features-Community-Left.JPG"
            alt="Community overview"
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6 }}
            sx={{
              position: "absolute",
              borderRadius: 3,
              boxShadow: "0 30px 80px rgba(0,0,0,0.45)",
              left: { xs: "10%", md: "0%" },
              top: { xs: "0%", md: "0%" },
              width: { xs: "80%", md: "40%" },
              zIndex: { xs: 1, md: 1 },
              opacity: { xs: 1, md: 0.88 },
              border: "1px solid rgba(255,255,255,0.12)",
              backgroundColor: "rgba(255,255,255,0.02)",
            }}
          />

          <Box
            component={motion.img}
            src="/Features/Features-Community-Center.JPG"
            alt="Community database details"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            sx={{
              position: "absolute",
              borderRadius: 3,
              boxShadow: "0 40px 120px rgba(0,0,0,0.6)",
              left: { xs: "10%", md: "25%" },
              top: { xs: "30%", md: "20%" },
              width: { xs: "80%", md: "50%" },
              zIndex: 3,
              border: "1px solid rgba(255,255,255,0.14)",
              backgroundColor: "rgba(255,255,255,0.02)",
            }}
          />

          <Box
            component={motion.img}
            src="/Features/Features-Community-Right.JPG"
            alt="Community interaction history"
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6 }}
            sx={{
              position: "absolute",
              borderRadius: 3,
              boxShadow: "0 30px 80px rgba(0,0,0,0.45)",
              left: { xs: "10%", md: "60%" },
              top: { xs: "60%", md: "0%" },
              width: { xs: "80%", md: "40%" },
              zIndex: { xs: 2, md: 1 },
              opacity: { xs: 1, md: 0.88 },
              border: "1px solid rgba(255,255,255,0.12)",
              backgroundColor: "rgba(255,255,255,0.02)",
            }}
          />
        </Box>
      </Container>
    </Box>
  );
};

export default CommunitySection;